/*
 * Copyright 2018 Red Hat Inc. and/or its affiliates and other contributors
 * as indicated by the @author tags. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the 'License'); you may not
 * use this file except in compliance with the License. You may obtain a copy of
 * the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied. See the
 * License for the specific language governing permissions and limitations under
 * the License.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ContentAlert } from '../../helpers/ContentAlert';
import { audiencePresent, authServerUrl } from "../../helpers/KeycloakHelper";
export class AccountServiceClient {
    constructor(keycloak) {
        this.keycloak = keycloak;
        this.accountUrl = `${authServerUrl(keycloak)}realms/${this.keycloak.realm}/account`;
        this.credentialsUrl = `${authServerUrl(keycloak)}realms/${this.keycloak.realm}/account-extended`;
    }
    doGet(endpoint, params) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.doRequest(endpoint, Object.assign(Object.assign({}, params), { method: 'get' }));
        });
    }
    doDelete(endpoint, params) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.doRequest(endpoint, Object.assign(Object.assign({}, params), { method: 'delete' }));
        });
    }
    doRequest(endpoint, params) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.keycloak.updateToken(5);
            const config = this.makeConfig(params);
            const url = this.makeUrl(endpoint, params).toString();
            const response = yield fetch(url, config);
            const data = yield response.json();
            if (!response.ok) {
                this.handleError(response, data);
            }
            return data;
        });
    }
    handleError(response, data) {
        if (response !== null && response.status === 401) {
            if (this.keycloak.authenticated && !audiencePresent(this.keycloak)) {
                window.location.href = baseUrl + '#/forbidden';
                AccountServiceClient.showErrorHint(response, data);
            }
            else {
                this.keycloak.login();
            }
        }
        if (response !== null && response.status === 403) {
            window.location.href = baseUrl + '#/forbidden';
            AccountServiceClient.showErrorHint(response, data);
        }
    }
    static showErrorHint(response, data) {
        if (response !== null) {
            if (data && data['errors'] != null) {
                for (const err of data['errors']) {
                    ContentAlert.danger(err['errorMessage'], err['params']);
                }
            }
            else {
                ContentAlert.danger(`${response.statusText}: ${data['errorMessage'] ? data['errorMessage'] : ''} ${data['error'] ? data['error'] : ''}`);
            }
        }
    }
    makeUrl(endpoint, config) {
        if (endpoint.startsWith('http')) {
            return new URL(endpoint);
        }
        let url = new URL(this.accountUrl + endpoint);
        if (endpoint.startsWith("/credentials")) {
            url = new URL(this.credentialsUrl + endpoint);
        }
        if (config && config.hasOwnProperty('params')) {
            const params = config.params || {};
            Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));
        }
        return url;
    }
    makeConfig(params = {}) {
        return Object.assign(Object.assign({}, params), { headers: Object.assign(Object.assign({ 'Content-Type': 'application/json' }, params.headers), { Authorization: 'Bearer ' + this.keycloak.token }) });
    }
}
